import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Play, Clipboard, Award, ShieldCheck, Youtube, Download, CheckCircle, Activity, ChevronRight, Loader2 } from 'lucide-react';
import React, { useMemo } from 'react';
import { Gnb, Footer } from '@/components/shared/layout';
import { Card, CardHeader, CardContent, CardFooter, Button, Badge } from '@/components/shared/ui';
import { buttonVariants } from '@/components/shared/ui/Button';
import { cn } from '@/lib/cn';
import keyVisual from '../../assets/img/key_visual.png';
import practiceVR from '../../assets/img/practice_VR_01.jpeg';
import practiceMobile from '../../assets/img/practice_mobile_new.png';
import metaIcon from '../../assets/img/Meta.png';
import googlePlayIcon from '../../assets/img/googleplay.png';
import appleIcon from '../../assets/img/apple_logo.png';
import brochureEnPDF from '../../assets/files/NEWBASE_Catalog_en.pdf';
import brochureKoPDF from '../../assets/files/NEWBASE_Catalog_ko.pdf';
import { useOpenClassList } from '@/data/queries/useClasses';
import { useAuth } from '@/contexts/AuthContext';
import { ClassItem } from '@/data/classes';
import { ROUTES } from '@/lib/constants/routes';
import type { OpenClassListItemDto } from '@/types/api/class';
import {
  getLocalizedClassItem,
  isOpenClassItem,
  isOpenClassListEntryEnded,
  openClassListItemDtoToClassItem,
} from '@/utils/classUtils';
import {
  LANDING_OPEN_CLASS_PREVIEW_COUNT,
  OPEN_CLASS_CLIENT_LIST_BATCH_SIZE,
} from '@/constants/openClassList';

function OpenClassPreviewSection(): React.ReactElement {
  const { t, i18n } = useTranslation(['landing', 'class']);
  const navigate = useNavigate();
  const { user, getCurrentRole } = useAuth();
  const role = getCurrentRole();
  const isLoggedIn = user !== null;
  const basePath = role === 'admin' ? '/admin' : role === 'master' ? '/master' : '/student';

  const openListParams = useMemo(
    () => ({ page: 1, pageSize: OPEN_CLASS_CLIENT_LIST_BATCH_SIZE }),
    []
  );

  const { data, isLoading, isError } = useOpenClassList(openListParams);

  const previewItems = useMemo(() => {
    const rawList: OpenClassListItemDto[] = data?.classList ?? [];
    const allClasses = rawList
      .filter((dto) => !isOpenClassListEntryEnded(dto as OpenClassListItemDto & Record<string, unknown>))
      .map((dto) => openClassListItemDtoToClassItem(dto));

    let filtered = allClasses.filter((item: ClassItem) => item.isActive !== false);
    filtered = filtered.filter(
      (item: ClassItem) =>
        item.classType === '오픈' ||
        item.classType === 'open' ||
        isOpenClassItem(item)
    );
    return filtered.slice(0, LANDING_OPEN_CLASS_PREVIEW_COUNT);
  }, [data?.classList]);

  return (
    <section className="bg-white border-t border-gray-100 py-16 md:py-20">
      <div className="container" style={{ maxWidth: '1280px' }}>
        <div className="mb-10 text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {t('openClass.sectionTitle')}
            </h2>
            <p className="text-base text-[var(--text-sub)]">
              {t('openClass.sectionDesc')}
            </p>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-500">
            <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
            <span className="text-sm">{t('class:loading')}</span>
          </div>
        )}

        {!isLoading && isError && (
          <p className="text-sm text-gray-500 py-8 text-center">{t('class:openClassListLoadError')}</p>
        )}

        {!isLoading && !isError && previewItems.length === 0 && (
          <p className="text-center text-gray-500 py-12">{t('class:noCategoryClasses')}</p>
        )}

        {!isLoading && !isError && previewItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {previewItems.map((item: ClassItem) => {
              const displayItem = getLocalizedClassItem(item as ClassItem, i18n.language);
              const isEnrolled = (() => {
                if (item.apiEnrollment === true) return true;
                if (item.apiEnrollment === false) return false;
                if (!isLoggedIn) return false;
                const participatingClasses = JSON.parse(localStorage.getItem('participatingClasses') || '{}') as Record<string, boolean>;
                try {
                  const raw = localStorage.getItem('myClassStatus') || '{}';
                  const myClassStatusMap = JSON.parse(raw) as Record<string, string>;
                  return !!participatingClasses[item.id] || !!myClassStatusMap[item.id];
                } catch {
                  return !!participatingClasses[item.id];
                }
              })();

              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!isLoggedIn) {
                      navigate(`/open-class/${item.id}`);
                      return;
                    }
                    if (role === 'admin') {
                      navigate(`/open-class/${item.id}`);
                      return;
                    }
                    if (isEnrolled) {
                      navigate(`${basePath}/my-classes/${item.id}`);
                    } else {
                      navigate(`/open-class/${item.id}`);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }
                  }}
                  className="relative w-full aspect-[5/5] max-h-[360px] bg-gray-100 rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                >
                  {displayItem.thumbnail ? (
                    <img
                      src={displayItem.thumbnail}
                      alt=""
                      className="absolute inset-0 z-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="absolute inset-0 z-0 flex items-center justify-center bg-gray-100 text-sm text-gray-400">
                      No Image
                    </div>
                  )}
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-36 bg-gradient-to-b from-black/88 via-black/55 to-transparent"
                    aria-hidden
                  />
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] px-4 pb-10 pt-4">
                    <h3 className="max-w-[88%] text-base font-bold leading-snug text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.95),0_2px_16px_rgb(0_0_0/0.55),0_0_1px_rgb(0_0_0/1)] line-clamp-3 md:text-lg">
                      {displayItem.title}
                    </h3>
                  </div>
                  {(isEnrolled || displayItem.isNew || displayItem.isRecommended || displayItem.price === 0) && (
                    <div className="absolute right-3 top-3 z-[3] flex max-w-[58%] flex-wrap justify-end gap-1.5">
                      {isEnrolled && (
                        <span className="inline-flex items-center rounded-full bg-blue-600 px-2.5 py-0.5 text-[11px] font-bold text-white shadow-sm">
                          {t('class:enrolled')}
                        </span>
                      )}
                      {displayItem.isNew && (
                        <span className="inline-flex items-center rounded-full bg-green-500 px-2.5 py-0.5 text-[11px] font-bold text-white">
                          NEW
                        </span>
                      )}
                      {displayItem.isRecommended && (
                        <span className="inline-flex items-center rounded-full bg-orange-500 px-2.5 py-0.5 text-[11px] font-bold text-white">
                          {t('class:badgeRecommended')}
                        </span>
                      )}
                      {displayItem.price === 0 && (
                        <span className="inline-flex items-center rounded-full bg-purple-500 px-2.5 py-0.5 text-[11px] font-bold text-white">
                          {t('class:badgeFree')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => navigate(ROUTES.COMMON.CLASS_LIST)}
          >
            {t('openClass.viewMore')}
            <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage(): React.ReactElement {
  const { t, i18n } = useTranslation('landing');
  const brochure =
    (i18n.resolvedLanguage || i18n.language).split('-')[0] === 'ko'
      ? { href: brochureKoPDF, filename: 'NEWBASE_Catalog_ko.pdf' }
      : { href: brochureEnPDF, filename: 'NEWBASE_Catalog_en.pdf' };
  const platformVrLabel = t('product.platformVr');
  const platformMobileLabel = t('product.platformMobile');
  const platformMobileBaseLabel = platformMobileLabel.replace(/\s*(모바일|mobile)$/i, ' Mobile');
  const productCards = [
    {
      id: 'vr',
      image: practiceVR,
      imageAlt: t('product.vrTitle'),
      title: platformVrLabel,
      description: t('product.vrDesc'),
      links: [
        {
          href: 'https://www.meta.com/ko-kr/experiences/medicrew/7247166375308400/?ranking_trace=0_7247166375308400_QUESTSEARCH_251bfbc9-1af0-491b-8a11-c547ee9ee833&utm_source=www.medicrew.me&utm_medium=oculusredirect',
          label: t('product.metaStore'),
          icon: metaIcon,
        },
      ],
    },
    {
      id: 'mobile',
      image: practiceMobile,
      imageAlt: t('product.mobileTitle'),
      imageClassName: 'object-[center_65%]',
      title: platformMobileBaseLabel,
      description: t('product.mobileDesc'),
      links: [
        {
          href: 'https://apps.apple.com/gh/app/medicrew/id1641856610',
          label: t('product.appStore'),
          icon: appleIcon,
        },
        {
          href: 'https://play.google.com/store/apps/details?id=com.newbase.MedicrewPlatform&pcampaignid=web_share',
          label: t('product.googlePlay'),
          icon: googlePlayIcon,
        },
      ],
    },
  ];

  return (
    <div className="app-container">
      {/* 1. GNB (Navbar) */}
      <Gnb />

      {/* 2. Hero Section */}
      <section className="bg-white pb-16">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="relative w-full mt-16 rounded-3xl overflow-hidden mb-8">
            <img
              src={keyVisual}
              alt={t('hero.aclsBannerTitle')}
              className="w-full object-cover block"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/70 via-brand-500/10 to-gray-900/50" aria-hidden />
            <div className="absolute inset-0 z-10 flex flex-col justify-center p-6 text-white md:p-10 lg:p-12">
              <h2 className="max-w-full md:max-w-2xl whitespace-pre-line text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight tracking-tight drop-shadow-md">
                {t('hero.overlayTitle')}
              </h2>
            </div>
          </div> 
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-base font-semibold md:text-lg text-gray-700 max-w-2xl whitespace-pre-line">
              {t('hero.subtitle')}
            </div>
            <a
              href={brochure.href}
              download={brochure.filename}
              className={cn(buttonVariants({ variant: 'dark', size: 'md' }), 'gap-2')}
            >
              <Download size={18} />
              {t('hero.catalogDownload')}
            </a>
          </div>
        </div>
      </section>

      <OpenClassPreviewSection />

      {/* 3. 3 Steps to Use Section */}
      <section className="py-16 md:py-24 bg-blue-50">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('courses.sectionTitle')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white border border-blue-500">
              <CardContent className="p-8">
                <Badge className="bg-blue-500 text-white mb-6 text-xs font-bold px-3 py-1">
                  {t('courses.step1')}
                </Badge>
                <h3 className="text-xl font-bold mb-4">{t('courses.step1Title')}</h3>
                <p className="text-sm text-[var(--text-sub)] leading-relaxed whitespace-pre-line">
                  {t('courses.step1Desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-blue-500">
              <CardContent className="p-8">
                <Badge className="bg-blue-500 text-white mb-6 text-xs font-bold px-3 py-1">
                  {t('courses.step2')}
                </Badge>
                <h3 className="text-xl font-bold mb-4">{t('courses.step2Title')}</h3>
                <p className="text-sm text-[var(--text-sub)] leading-relaxed whitespace-pre-line">
                  {t('courses.step2Desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-blue-500">
              <CardContent className="p-8">
                <Badge className="bg-blue-500 text-white mb-6 text-xs font-bold px-3 py-1">
                  {t('courses.step3')}
                </Badge>
                <h3 className="text-xl font-bold mb-4">{t('courses.step3Title')}</h3>
                <p className="text-sm text-[var(--text-sub)] leading-relaxed whitespace-pre-line">
                  {t('courses.step3Desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 4. Product Types Section */}
      <section className="py-20 bg-white">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight whitespace-pre-line">
              {t('product.hybridTitle')}
            </h2>
            <p className="text-base text-[var(--text-sub)] leading-relaxed">
              {t('product.hybridDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {productCards.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden border-gray-100 bg-white shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-60 overflow-hidden bg-gray-100 md:h-64">
                  <img
                    src={product.image}
                    alt={product.imageAlt}
                    className={cn('h-full w-full object-cover', product.imageClassName)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" aria-hidden />
                  <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="text-2xl font-bold text-white md:text-3xl">{product.title}</h3>
                  </div>
                </div>
                <CardContent className="flex min-h-[220px] flex-col p-6 md:p-8">
                  <p className="mb-5 text-sm leading-relaxed text-[var(--text-sub)] md:text-base">
                    {product.description}
                  </p>
                  <div className={cn('mt-auto grid gap-2', product.links.length > 1 && 'sm:grid-cols-2')}>
                    {product.links.map((link) => (
                      <a
                        key={link.label}
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={link.label}
                        className="flex h-14 items-center gap-2 rounded-md border border-gray-200 bg-white px-3 text-gray-800 shadow-sm transition-all hover:-translate-y-0.5 hover:text-gray-800 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center">
                          <img src={link.icon} alt="" className="h-7 w-7 object-contain" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1 text-start text-sm font-bold leading-snug">
                          {link.label}
                        </span>
                        <ChevronRight size={16} className="shrink-0 text-gray-700" aria-hidden />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Demo Video Section */}
      <section className="py-20 bg-gray-50">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="mb-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('demo.title')}</h2>
            <p className="mx-auto max-w-2xl text-base text-[var(--text-sub)]">
              {t('demo.desc')}
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl bg-black shadow-xl">
            <div className="relative aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/e___ySNIa9A?si=DwZgsiiHmKyMCT-y"
                title={t('demo.videoTitle')}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* 6. Stats Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-blue-50 to-white">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-bold mb-2">{t('stats.title')}</h2>
            <p className="text-base text-[var(--text-sub)]">{t('stats.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="text-center border-0 shadow-none transition-all hover:shadow-xl hover:-translate-y-1">
              <CardContent className="pt-8 pb-6">
                <div className="text-5xl font-bold text-brand-500 mb-2">
                  100<span className="text-2xl opacity-70">+</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('stats.globalClientsTitle')}</h3>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-none transition-all hover:shadow-xl hover:-translate-y-1">
              <CardContent className="pt-8 pb-6">
                <div className="text-5xl font-bold text-brand-500 mb-2">
                  13.2<span className="text-2xl opacity-70">%</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('stats.stat1Title')}</h3>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-none transition-all hover:shadow-xl hover:-translate-y-1">
              <CardContent className="pt-8 pb-6">
                <div className="text-5xl font-bold text-brand-500 mb-2">
                  98<span className="text-2xl opacity-70">%</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('stats.stat2Title')}</h3>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-none transition-all hover:shadow-xl hover:-translate-y-1">
              <CardContent className="pt-8 pb-6">
                <div className="text-5xl font-bold text-brand-500 mb-2">
                  100K<span className="text-2xl opacity-70">+</span>
                </div>
                <h3 className="text-lg font-bold mb-2">{t('stats.stat3Title')}</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 6. License Plans */}
      <section className="py-16 md:py-12 bg-white">
        <div className="container" style={{ maxWidth: '1280px' }}>
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-2">{t('license.sectionTitle')}</h2>
            <p className="text-lg text-[var(--text-sub)] max-w-2xl mx-auto">
              {t('license.sectionDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">

            {/* Individual Plan */}
            <Card className="flex flex-col bg-white border border-gray-200">
              <CardHeader>
                <h3 className="text-xl font-bold mb-1">{t('license.basicTitle')}</h3>
                <p className="text-sm text-[var(--text-sub)] whitespace-pre-line">{t('license.basicDesc')}</p>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6 pb-6">
                  <span className="text-2xl font-bold text-[var(--text-main)]">{t('license.basicPrice')}</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.basicFeature1')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.basicFeature2')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.basicFeature3')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.basicFeature4')}
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Link
                  to={ROUTES.COMMON.CLASS_LIST}
                  className={cn(buttonVariants({ variant: 'primary', size: 'md' }), 'w-full')}
                >
                  {t('license.individualPlanCta')}
                </Link>
              </CardFooter>
            </Card>

            {/* Class Plan */}
            <Card className="flex flex-col bg-white border border-gray-200">
              <CardHeader>
                <h3 className="text-xl font-bold mb-1">{t('license.classTitle')}</h3>
                <p className="text-sm text-[var(--text-sub)] whitespace-pre-line">{t('license.classDesc')}</p>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6 pb-6">
                  <span className="text-2xl font-bold text-[var(--text-main)]">{t('license.classPrice')}</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature1')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature2')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature3')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature4')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature5')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature6')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.classFeature7')}
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Link
                  to={ROUTES.COMMON.CONTACT}
                  className={cn(buttonVariants({ variant: 'primary', size: 'md' }), 'w-full')}
                >
                  {t('license.inquiryCta')}
                </Link>
              </CardFooter>
            </Card>

            {/* Custom Plan */}
            <Card className="flex flex-col bg-white border border-gray-200">
              <CardHeader>
                <h3 className="text-xl font-bold mb-1">{t('license.enterpriseTitle')}</h3>
                <p className="text-sm text-[var(--text-sub)]">{t('license.enterpriseDesc')}</p>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6 pb-6">
                  <span className="text-2xl font-bold text-[var(--text-main)]">{t('license.enterprisePrice')}</span>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.enterpriseFeature1')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.enterpriseFeature2')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.enterpriseFeature3')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.enterpriseFeature4')}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-[var(--text-sub)]">
                    <CheckCircle size={16} className="shrink-0 text-brand-500" aria-hidden />{t('license.enterpriseFeature5')}
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Link
                  to={ROUTES.COMMON.CONTACT}
                  className={cn(buttonVariants({ variant: 'outline', size: 'md' }), 'w-full')}
                >
                  {t('license.inquiryCta')}
                </Link>
              </CardFooter>
            </Card>
          </div>

        </div>
      </section>

      {/* 7. Footer */}
      <Footer />
    </div>
  );
};